import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../lib/apiBase';

const STAGE_LABELS = {
  received: 'Received',
  in_review: 'In Review',
  attorney_comments_added: 'Attorney Comments Added',
  ready_for_download: 'Ready for Download',
  completed: 'Completed',
};

function formatDue(hoursRemaining, isOverdue) {
  if (hoursRemaining == null) return '—';
  if (isOverdue || hoursRemaining < 0) {
    return `${Math.abs(hoursRemaining).toFixed(1)}h overdue`;
  }
  return `${hoursRemaining.toFixed(1)}h left`;
}

export default function AttorneyAssistOrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [notes, setNotes] = useState({});

  const token = () => localStorage.getItem('token');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/attorney-assist/admin/orders`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error('Failed to load orders');
      const data = await res.json();
      setOrders(data.orders || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const advance = async (orderId) => {
    setBusyId(orderId);
    try {
      const res = await fetch(
        `${API_BASE}/api/attorney-assist/admin/orders/${orderId}/advance`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ note: notes[orderId] || null }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Advance failed');
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const uploadReviewed = async (orderId, file) => {
    if (!file) return;
    setBusyId(orderId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(
        `${API_BASE}/api/attorney-assist/admin/orders/${orderId}/upload-reviewed`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token()}` },
          body: fd,
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 shadow text-slate-600">
        <i className="fas fa-spinner fa-spin mr-2" /> Loading Attorney Assist orders…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow">
        <h2 className="text-xl font-bold text-slate-900 mb-1">Attorney Assist Orders</h2>
        <p className="text-sm text-slate-600 mb-4">
          UOA Legal queue — advance status linearly. Upload the reviewed file before moving to Ready
          for Download. Flags show orders within 12h of the 48h deadline.
        </p>
        {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
        {orders.length === 0 && (
          <p className="text-slate-500 text-sm">No paid Assist orders yet.</p>
        )}
      </div>

      {orders.map((o) => {
        const overdue = o.is_overdue;
        const approaching = o.approaching_deadline;
        const canAdvance = o.status !== 'completed';
        const nextNeedsFile =
          o.status === 'attorney_comments_added' && !o.has_reviewed_file;

        return (
          <div
            key={o.id}
            className={`bg-white rounded-xl p-6 shadow border-l-4 ${
              overdue
                ? 'border-red-500'
                : approaching
                  ? 'border-amber-400'
                  : 'border-slate-200'
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <div className="font-mono text-sm text-slate-500">{o.id}</div>
                <div className="text-lg font-semibold text-slate-900">
                  {o.client_name}{' '}
                  <span className="text-sm font-normal text-slate-500">{o.client_email}</span>
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  {STAGE_LABELS[o.status] || o.status_label} · {o.tier} · $
                  {(o.amount_cents / 100).toFixed(0)}
                  {o.pathway_hint ? ` · ${o.pathway_hint}` : ''}
                </div>
              </div>
              <div className="text-right text-sm">
                {(overdue || approaching) && (
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-xs font-semibold mb-1 ${
                      overdue ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-900'
                    }`}
                  >
                    {overdue ? 'OVERDUE' : 'APPROACHING'}
                  </span>
                )}
                <div className="text-slate-600">
                  SLA: {formatDue(o.hours_remaining, overdue)}
                </div>
                <a
                  href={`/track/${o.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-amber-700 hover:underline text-xs"
                >
                  Open tracking page
                </a>
              </div>
            </div>

            <div className="text-xs text-slate-500 mb-3">
              Draft: {o.draft_filename || '—'}
              {o.has_reviewed_file && (
                <span className="ml-3 text-green-700">
                  Reviewed file: {o.reviewed_filename}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-slate-500 mb-1">Upload reviewed file</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  disabled={busyId === o.id || o.status === 'completed'}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadReviewed(o.id, f);
                    e.target.value = '';
                  }}
                  className="text-sm w-full"
                />
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className="block text-xs text-slate-500 mb-1">Note (optional)</label>
                <input
                  value={notes[o.id] || ''}
                  onChange={(e) => setNotes((n) => ({ ...n, [o.id]: e.target.value }))}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  placeholder="Internal / client note"
                />
              </div>
              <button
                type="button"
                disabled={!canAdvance || busyId === o.id}
                onClick={() => advance(o.id)}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-slate-800 disabled:opacity-50"
                title={
                  nextNeedsFile
                    ? 'Upload reviewed file first, then advance'
                    : 'Advance to next stage'
                }
              >
                {busyId === o.id ? 'Working…' : 'Advance status'}
              </button>
            </div>
            {nextNeedsFile && (
              <p className="text-xs text-amber-800 mt-2">
                Upload the reviewed file before advancing to Ready for Download.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
